/*
 * gramms LP1 — UGC strip.
 *
 * Figma asks for a loop, QA asked for it to be movable by hand. A CSS
 * transform marquee can only do the first: the track is transformed and the
 * rail is clipped, so there is nothing for a finger to grab.
 *
 * So the loop moves from `transform` to `scrollLeft`. The rail becomes a real
 * scroll container — touch, trackpad and keyboard come for free — and a rAF
 * loop nudges scrollLeft at exactly the speed the CSS animation used, read off
 * the same --lp1-ugc-duration. Whatever the user does wins; the loop picks up
 * again a moment after they let go.
 *
 * The CSS marquee stays as the no-JS fallback. This file opts in by adding
 * --live, which is the class that turns the animation off.
 */
(function () {
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Long enough for iOS momentum to land before the loop takes the wheel
     back, short enough that a strip left alone starts moving again. */
  var RESUME_DELAY = 1200;
  /* Past this a pointer gesture is a drag, not a click on a clip. */
  var DRAG_SLOP = 4;

  function bind(rail) {
    var track = rail.querySelector('.lp1-reviews__ugc-track');
    var group = rail.querySelector('.lp1-reviews__ugc-group');
    if (!track || !group) return;

    var width = 0; /* one group */
    var speed = 0; /* px per second */
    var pos = 0; /* our own sub-pixel position */
    var last = 0;
    var holding = false;
    var hovering = false;
    var dragging = false;
    var pending = false;
    var moved = false;
    var dragX = 0;
    var dragFrom = 0;
    var resumeAt = 0;
    var frame = null;
    var playing = 0;

    function measure() {
      width = group.getBoundingClientRect().width;
      var seconds = parseFloat(getComputedStyle(rail).getPropertyValue('--lp1-ugc-duration'));
      if (!(seconds > 0)) seconds = 60;
      /* The CSS keyframe travels 25% of a four-copy track — one group — over
         the full duration. Same speed, so the two are interchangeable. */
      speed = reduce.matches ? 0 : width / seconds;
    }

    /* The track is four identical copies, so shifting by exactly one group is
       invisible. Parking between the first and second copy leaves a whole
       group of runway on either side to drag into. */
    function wrap() {
      if (width <= 0) return false;
      if (rail.scrollLeft >= width * 2) {
        rail.scrollLeft -= width;
        return true;
      }
      if (rail.scrollLeft < width) {
        rail.scrollLeft += width;
        return true;
      }
      return false;
    }

    function tick(now) {
      frame = window.requestAnimationFrame(tick);

      var dt = last ? (now - last) / 1000 : 0;
      last = now;

      /* A backgrounded tab hands back one enormous frame; spending it would
         teleport the strip. */
      if (dt <= 0 || dt > 0.25) return;
      if (speed <= 0 || holding || hovering || playing > 0 || document.hidden) return;
      if (now < resumeAt) return;

      pos += speed * dt;
      rail.scrollLeft = pos;
      /* Only resync on a wrap: reading scrollLeft back every frame would lose
         the fraction wherever the browser rounds it, and at ~0.5px a frame
         that means never moving at all. */
      if (wrap()) pos = rail.scrollLeft;
    }

    function hold() {
      holding = true;
      pos = rail.scrollLeft;
    }

    function release(event) {
      pending = false;
      if (dragging) {
        dragging = false;
        if (event && event.pointerId != null) {
          try { rail.releasePointerCapture(event.pointerId); } catch (error) { /* already gone */ }
        }
        rail.classList.remove('lp1-reviews__ugc--dragging');
      }
      holding = false;
      pos = rail.scrollLeft;
      resumeAt = (window.performance ? performance.now() : Date.now()) + RESUME_DELAY;
    }

    /*
     * Un boton de play dentro de una copia aria-hidden no puede recibir foco:
     * el navegador se niega a aplicar el aria-hidden y lo reporta en consola.
     * Las copias son identicas y visibles, asi que volverlas inert las dejaria
     * muertas al click — que es peor. Quitarle el foco al mouse resuelve las
     * dos cosas y deja intacto el camino de teclado, que solo ve la copia 1.
     */
    rail.addEventListener('mousedown', function (event) {
      var button = event.target.closest && event.target.closest('[data-lp1-ugc-play]');
      if (button) event.preventDefault();
    });

    rail.addEventListener('pointerdown', function (event) {
      hold();
      moved = false;
      /* Touch already scrolls natively. Dragging is only wired up for a mouse,
         which otherwise has no way to move a horizontal rail. */
      if (event.pointerType !== 'mouse') return;
      pending = true;
      dragX = event.clientX;
      dragFrom = rail.scrollLeft;
    });

    rail.addEventListener('pointermove', function (event) {
      if (!pending && !dragging) return;
      var delta = event.clientX - dragX;

      /*
       * La captura de puntero no se toma hasta que hay arrastre de verdad.
       * Tomandola en el pointerdown, el `click` se despacha sobre el rail y no
       * sobre el boton, asi que `closest('[data-lp1-ugc-play]')` daba null y
       * el play no se enteraba nunca. Ese era el "doy click y no hace nada".
       */
      if (!dragging) {
        if (Math.abs(delta) <= DRAG_SLOP) return;
        dragging = true;
        moved = true;
        rail.classList.add('lp1-reviews__ugc--dragging');
        try { rail.setPointerCapture(event.pointerId); } catch (error) { /* not captureable */ }
      }

      event.preventDefault();
      rail.scrollLeft = dragFrom - delta;
      pos = rail.scrollLeft;
    });

    rail.addEventListener('pointerup', release);
    rail.addEventListener('pointercancel', release);

    /* A drag that ends on a clip must not also open it. */
    rail.addEventListener('click', function (event) {
      if (!moved) return;
      moved = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    /*
     * Play. The poster lives on the <video> itself, so starting it is just a
     * play() call — nothing to swap in. Controls only appear once it is
     * running: before that the strip is a wall of thumbnails and a row of
     * native control bars would bury the design.
     */
    rail.addEventListener('click', function (event) {
      var button = event.target.closest && event.target.closest('[data-lp1-ugc-play]');
      if (!button) return;
      var item = button.closest('.lp1-reviews__ugc-item');
      var video = item && item.querySelector('[data-lp1-ugc-video]');
      if (!video) return;

      event.preventDefault();

      /* Two clips talking over each other helps nobody. */
      Array.prototype.forEach.call(rail.querySelectorAll('[data-lp1-ugc-video]'), function (other) {
        if (other !== video && !other.paused) other.pause();
      });

      item.classList.add('lp1-reviews__ugc-item--playing');
      video.controls = true;
      var started = video.play();
      if (started && started.catch) {
        started.catch(function (error) {
          /* Autoplay policy, a dead source, a codec the browser will not take.
             Swallowing this silently is what makes "the click does nothing"
             impossible to chase. */
          item.classList.remove('lp1-reviews__ugc-item--playing');
          video.controls = false;
          if (window.console) console.warn('[lp1-ugc] no se pudo reproducir', video.currentSrc || video.src, error);
        });
      }
    });

    /* play and pause do not bubble, so these listen on the way down. */
    rail.addEventListener('play', function () {
      playing += 1;
      pos = rail.scrollLeft;
    }, true);

    function released() {
      playing = Math.max(0, playing - 1);
      pos = rail.scrollLeft;
    }

    rail.addEventListener('pause', released, true);
    rail.addEventListener('ended', released, true);

    rail.addEventListener('wheel', function () {
      pos = rail.scrollLeft;
      resumeAt = (window.performance ? performance.now() : Date.now()) + RESUME_DELAY;
    }, { passive: true });

    /* Same courtesy the CSS had: stop while it is being read or tabbed. */
    rail.addEventListener('mouseenter', function () { hovering = true; });
    rail.addEventListener('mouseleave', function () { hovering = false; pos = rail.scrollLeft; });
    rail.addEventListener('focusin', function () { hovering = true; });
    rail.addEventListener('focusout', function () { hovering = false; pos = rail.scrollLeft; });

    function start() {
      measure();
      /* Wrapping by one group only stays invisible while the viewport shows
         less than the two copies sitting ahead of the parked position. A rail
         wider than that keeps the CSS marquee, which is no worse off. */
      if (width <= 0 || rail.clientWidth > width * 2) {
        rail.classList.remove('lp1-reviews__ugc--live');
        if (frame) { window.cancelAnimationFrame(frame); frame = null; }
        return;
      }
      rail.classList.add('lp1-reviews__ugc--live');
      rail.scrollLeft = width;
      pos = rail.scrollLeft;
      last = 0;
      if (!frame) frame = window.requestAnimationFrame(tick);
    }

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(start, 150);
    });

    if (reduce.addEventListener) reduce.addEventListener('change', measure);

    /* The clips are lazy images: measuring before they land reads a collapsed
       group and the speed comes out wrong. */
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start);
  }

  function setup(root) {
    if (!root || root.dataset.lp1UgcReady === 'true') return;
    root.dataset.lp1UgcReady = 'true';
    bind(root);
  }

  function setupAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('.lp1-reviews__ugc'), setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setupAll(); });
  } else {
    setupAll();
  }

  document.addEventListener('shopify:section:load', function (event) { setupAll(event.target); });
})();
